import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  FiMail, FiSave, FiShield, FiLock, FiEye, FiEyeOff, FiEdit3,
  FiPhone, FiGlobe, FiMapPin, FiUpload, FiTrash2,
  FiCheck, FiImage, FiVideo
} from 'react-icons/fi';

/* ─────────────────────────────────────────────────────────
   Global CSS
───────────────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap');

.pr-root {
  font-family: 'Plus Jakarta Sans', sans-serif;
  --ink: #0c0d14;
  --ink2: #3d3f56;
  --muted: #878aa8;
  --accent: #5b5ef4;
  --accent-b: #7c3aed;
  --accent-glow: rgba(91,94,244,0.18);
  --success: #16a34a;
  --danger: #dc2626;
  --warn: #d97706;
  --surface: #ffffff;
  --surface2: #f7f8fc;
  --surface3: #eff0fb;
  --border: #e3e4f0;
  --radius: 18px;
  --radius-sm: 12px;
  --radius-xs: 8px;
  min-height: 100vh;
  background: #f3f4fc;
  position: relative;
}

.pr-root::before {
  content: '';
  position: fixed;
  inset: 0;
  background:
    radial-gradient(ellipse 800px 600px at 20% -10%, rgba(91,94,244,0.08) 0%, transparent 65%),
    radial-gradient(ellipse 600px 500px at 85% 90%, rgba(124,58,237,0.06) 0%, transparent 65%);
  pointer-events: none;
  z-index: 0;
}

.pr-inner {
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 40px 24px 100px;
}

/* ─── Hero strip ─── */
.pr-hero {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  padding: 0 4px;
}

.pr-av {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(140deg, #5b5ef4, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Instrument Serif', serif;
  font-size: 26px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 6px 24px rgba(91,94,244,0.32);
  border: 2.5px solid rgba(255,255,255,0.7);
}

.pr-hero-text h1 {
  margin: 0 0 3px;
  font-size: 22px;
  font-weight: 800;
  color: var(--ink);
  letter-spacing: -0.025em;
}

.pr-hero-text p {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.role-chip {
  background: var(--surface3);
  border: 1px solid #d0d1f0;
  color: var(--accent);
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ─── Card shell ─── */
.pr-card {
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255,255,255,0.95);
  border-radius: 24px;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 8px 40px rgba(12,13,20,0.09),
    0 2px 8px rgba(12,13,20,0.05);
  overflow: hidden;
}

/* ─── Tabs ─── */
.pr-tabs {
  display: flex;
  padding: 10px 10px 0;
  background: var(--surface2);
  border-bottom: 1px solid var(--border);
  gap: 2px;
}

.pr-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 11px 20px;
  border: none;
  background: none;
  border-radius: 12px 12px 0 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.18s;
}
.pr-tab:hover { color: var(--ink2); background: rgba(91,94,244,0.04); }
.pr-tab.on {
  color: var(--accent);
  font-weight: 700;
  background: var(--surface);
}
.pr-tab.on::after {
  content: '';
  position: absolute;
  bottom: -1px; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--accent), var(--accent-b));
  border-radius: 2px 2px 0 0;
}

/* ═══════════════════════════════════════════
   PREVIEW BANNER (top)
═══════════════════════════════════════════ */
.sig-preview-top {
  padding: 28px 32px 24px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(160deg, #f4f5fd 0%, #ffffff 55%, #f0eeff 100%);
}

.preview-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.preview-lbl {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}

.live-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #22c55e;
  animation: livePulse 2s infinite;
}
@keyframes livePulse {
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
  70%  { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}

/* email mockup */
.email-mockup {
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 16px rgba(12,13,20,0.07);
}

.email-mockup-titlebar {
  background: var(--surface2);
  border-bottom: 1px solid var(--border);
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 7px;
}

.em-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

.email-mockup-meta {
  padding: 16px 20px 0;
}

.email-meta-row {
  display: flex;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 5px;
}
.email-meta-row strong { color: var(--ink2); }

.email-subject {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  padding: 10px 20px 0;
  letter-spacing: -0.01em;
}

.email-body-text {
  font-size: 13px;
  color: var(--ink2);
  line-height: 1.65;
  padding: 10px 20px 16px;
}

.sig-divider {
  margin: 0 20px 16px;
  border: none;
  border-top: 1px solid var(--border);
}

.sig-render-zone {
  padding: 0 20px 20px;
}

.sig-empty {
  padding: 24px 20px;
  text-align: center;
  color: var(--muted);
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.sig-empty svg { opacity: 0.22; }

/* ═══════════════════════════════════════════
   DETAILS SECTION (bottom of sig tab)
═══════════════════════════════════════════ */
.details-section {
  padding: 28px 32px 0;
}

.sec-hd {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 22px;
}

.sec-num {
  width: 24px; height: 24px;
  border-radius: 7px;
  background: var(--surface3);
  border: 1px solid #d0d1f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: var(--accent);
  flex-shrink: 0;
}

.sec-lbl {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--ink2);
}

.sec-line { flex: 1; height: 1px; background: var(--border); }

/* grid rows */
.fg-row   { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
.fg-row.s1 { grid-template-columns: 1fr; }
@media (max-width: 620px) { .fg-row { grid-template-columns: 1fr; } }

.fg { display: flex; flex-direction: column; }

.fg-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  margin-bottom: 6px;
}

.fi-wrap { position: relative; }

.fi-icon {
  position: absolute;
  left: 12px; top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  pointer-events: none;
  display: flex; align-items: center;
}

.f-input {
  width: 100%;
  padding: 10px 13px;
  border-radius: var(--radius-xs);
  border: 1.5px solid var(--border);
  background: var(--surface);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  color: var(--ink);
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.f-input.ico { padding-left: 36px; }
.f-input:hover  { border-color: #b0b2d8; }
.f-input:focus  { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
.f-input::placeholder { color: #c8c9e0; }

/* ─── Logo strip ─── */
.logo-section {
  padding: 20px 32px 28px;
  border-top: 1px dashed #e0e1ef;
  margin-top: 10px;
}

.logo-inner {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.logo-actions { flex: 1; min-width: 200px; }

.logo-hint {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.65;
  margin: 0 0 14px;
}

.logo-btns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.btn-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 16px;
  border-radius: var(--radius-xs);
  border: 1.5px solid var(--border);
  background: var(--surface);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px; font-weight: 600;
  color: var(--ink2);
  cursor: pointer;
  transition: all 0.18s;
}
.btn-ghost:hover { border-color: var(--accent); color: var(--accent); background: var(--surface3); }

.btn-danger-ghost {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 16px;
  border-radius: var(--radius-xs);
  border: 1.5px solid #fecaca;
  background: #fff5f5;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12.5px; font-weight: 600;
  color: var(--danger);
  cursor: pointer;
  transition: all 0.18s;
}
.btn-danger-ghost:hover { background: #fee2e2; border-color: var(--danger); }

.logo-thumb-box {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.logo-thumb {
  width: 70px; height: 70px;
  border-radius: 12px;
  border: 2px solid var(--border);
  object-fit: contain;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.logo-empty-box {
  width: 70px; height: 70px;
  border-radius: 12px;
  border: 2px dashed var(--border);
  background: var(--surface2);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 4px;
  color: var(--muted);
  font-size: 9.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
}

.width-row { display: flex; align-items: center; gap: 12px; }

.width-lbl {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--muted); white-space: nowrap;
}

.width-num {
  width: 72px;
  padding: 7px 11px;
  border-radius: var(--radius-xs);
  border: 1.5px solid var(--border);
  background: var(--surface);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px; color: var(--ink);
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.width-num:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }

/* ─── Card footer ─── */
.pr-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 18px 32px;
  border-top: 1px solid var(--border);
  background: linear-gradient(135deg, rgba(247,248,252,0.7), rgba(255,255,255,0.5));
  gap: 12px;
}

/* ─── Primary button ─── */
.btn-prime {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 28px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-b) 100%);
  color: #fff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px; font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(91,94,244,0.32);
  transition: all 0.2s;
  letter-spacing: 0.01em;
}
.btn-prime:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(91,94,244,0.42); }
.btn-prime:active:not(:disabled) { transform: none; }
.btn-prime:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-prime.amber {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 16px rgba(217,119,6,0.3);
}
.btn-prime.amber:hover:not(:disabled) { box-shadow: 0 6px 22px rgba(217,119,6,0.4); }

/* ═══════════════════════════════════════════
   PASSWORD TAB
═══════════════════════════════════════════ */
.pw-card-head {
  display: flex; align-items: center; gap: 14px;
  padding: 24px 32px 20px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(135deg, rgba(247,248,252,0.8), rgba(255,255,255,0.5));
}

.ch-icon {
  width: 46px; height: 46px; border-radius: 13px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ch-icon.amber { background: linear-gradient(135deg,#fffbeb,#fef3c7); color: var(--warn); box-shadow: 0 3px 10px rgba(217,119,6,0.15); }

.ch-text h4 { margin: 0 0 3px; font-size: 16px; font-weight: 800; color: var(--ink); letter-spacing: -0.015em; }
.ch-text p  { margin: 0; font-size: 12.5px; color: var(--muted); line-height: 1.5; }

.pw-body { padding: 36px 32px 28px; }

.pw-form {
  max-width: 440px;
  display: flex; flex-direction: column; gap: 22px;
}

.pw-toggle-btn {
  position: absolute; right: 11px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer;
  color: var(--muted); display: flex; align-items: center;
  padding: 4px; border-radius: 6px; transition: color 0.15s;
}
.pw-toggle-btn:hover { color: var(--ink2); }

.str-bar { height: 3px; border-radius: 2px; background: var(--border); margin-top: 7px; overflow: hidden; }
.str-fill { height: 100%; border-radius: 2px; transition: width 0.3s, background 0.3s; }

.match-check {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  color: #22c55e; display: flex; pointer-events: none;
}

/* ─── Spinner ─── */
.spin-center { display: flex; align-items: center; justify-content: center; padding: 60px; }
.spin-ring {
  width: 32px; height: 32px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.65s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.ld { display: inline-block; animation: ldBlink 1.4s infinite both; }
.ld:nth-child(2) { animation-delay: 0.2s; }
.ld:nth-child(3) { animation-delay: 0.4s; }
@keyframes ldBlink { 0%,80%,100%{opacity:0} 40%{opacity:1} }
`;

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
const DEFAULT_FIELDS = {
  full_name: '', title: '', phone: '', mobile: '',
  email: '', website: '', address: '', logo_url: '', logo_width: '90'
};

function esc(s) {
  return (s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function buildSignatureHtml(f) {
  const hasLogo = f.logo_url && f.logo_url.trim();
  const hasInfo = f.full_name||f.title||f.phone||f.mobile||f.email||f.website||f.address;
  if (!hasLogo && !hasInfo) return '';
  const logoW = parseInt(f.logo_width,10)||90;
  const lines = [];
  if (f.full_name) lines.push(`<strong style="font-size:15px;color:#0c0d14;font-family:'Plus Jakarta Sans',sans-serif">${esc(f.full_name)}</strong>`);
  if (f.title)     lines.push(`<span style="font-size:12px;color:#878aa8;font-family:'Plus Jakarta Sans',sans-serif">${esc(f.title)}</span>`);
  if (f.phone)     lines.push(`<span style="font-size:12px;color:#3d3f56;font-family:'Plus Jakarta Sans',sans-serif">📞 ${esc(f.phone)}</span>`);
  if (f.mobile)    lines.push(`<span style="font-size:12px;color:#3d3f56;font-family:'Plus Jakarta Sans',sans-serif">📱 ${esc(f.mobile)}</span>`);
  if (f.email)     lines.push(`<span style="font-size:12px;color:#3d3f56;font-family:'Plus Jakarta Sans',sans-serif">✉️ <a href="mailto:${esc(f.email)}" style="color:#5b5ef4;text-decoration:none">${esc(f.email)}</a></span>`);
  if (f.website)   lines.push(`<span style="font-size:12px;color:#3d3f56;font-family:'Plus Jakarta Sans',sans-serif">🌐 <a href="${esc(f.website.startsWith('http')?f.website:'https://'+f.website)}" style="color:#5b5ef4;text-decoration:none">${esc(f.website)}</a></span>`);
  if (f.address)   lines.push(`<span style="font-size:12px;color:#3d3f56;font-family:'Plus Jakarta Sans',sans-serif">📍 ${esc(f.address)}</span>`);
  const infoCol = lines.join('<br/>');
  if (hasLogo && hasInfo) {
    return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tr>`
      +`<td valign="top" style="padding-right:16px;border-right:2px solid #e3e4f0">`
      +`<img src="${esc(f.logo_url)}" alt="Logo" width="${logoW}" style="display:block;max-width:${logoW}px"/></td>`
      +`<td valign="top" style="padding-left:16px">${infoCol}</td></tr></table>`;
  }
  if (hasLogo) return `<img src="${esc(f.logo_url)}" alt="Logo" width="${logoW}" style="display:block;max-width:${logoW}px"/>`;
  return `<div>${infoCol}</div>`;
}

function getPwStrength(pw) {
  if (!pw) return 0;
  let s=0;
  if (pw.length>=6) s++;
  if (pw.length>=10) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const STR_COL = ['#ef4444','#f97316','#eab308','#22c55e','#16a34a'];
const STR_W   = ['20%','40%','60%','80%','100%'];

/* ─────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────── */
function FF({ label, icon, value, onChange, placeholder, type='text' }) {
  return (
    <div className="fg">
      <span className="fg-label">{label}</span>
      <div className="fi-wrap">
        {icon && <span className="fi-icon">{icon}</span>}
        <input type={type} className={`f-input${icon?' ico':''}`}
          value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}/>
      </div>
    </div>
  );
}

const Dots = () => (
  <><span className="ld">.</span><span className="ld">.</span><span className="ld">.</span></>
);

/* ─────────────────────────────────────────────────────────
   Main
───────────────────────────────────────────────────────── */
const Profile = () => {
  const { user, loadUser } = useAuth();
  const { t } = useTranslation();
  const [sigFields, setSigFields]       = useState({ ...DEFAULT_FIELDS });
  const [sigLoading, setSigLoading]     = useState(true);
  const [sigSaving, setSigSaving]       = useState(false);
  const [tab, setTab]                   = useState('signature');
  const [zoomMeetingEmail, setZoomMeetingEmail] = useState('');
  const [zoomSaving, setZoomSaving]     = useState(false);
  const [logoError, setLogoError]       = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pwForm, setPwForm]             = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [pwLoading, setPwLoading]       = useState(false);
  const [showCur, setShowCur]           = useState(false);
  const [showNew, setShowNew]           = useState(false);

  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  useEffect(() => {
    setSigLoading(true);
    authAPI.getSignature()
      .then(res => {
        const d = res.data||{};
        if (d.signature_fields && typeof d.signature_fields==='object') {
          setSigFields(p=>({...p,...d.signature_fields}));
        } else if (!d.signature_fields && d.signature_html) {
          setSigFields(p=>({...p, full_name:user?.name||'', email:user?.email||''}));
        }
      })
      .catch(()=>{})
      .finally(()=>setSigLoading(false));
  }, [user]);

  useEffect(() => {
    setZoomMeetingEmail(user?.zoom_meeting_email != null ? String(user.zoom_meeting_email) : '');
  }, [user?.zoom_meeting_email, user?.user_id]);

  const saveZoomHostEmail = useCallback(async () => {
    setZoomSaving(true);
    try {
      await authAPI.updateProfile({ zoom_meeting_email: zoomMeetingEmail.trim() });
      toast.success(t('profile.toasts.zoomSaved'));
      await loadUser();
    } catch (err) {
      toast.error(err.response?.data?.message || t('profile.toasts.saveFailed'));
    }
    setZoomSaving(false);
  }, [zoomMeetingEmail, loadUser]);

  const sigHtml = useMemo(()=>buildSignatureHtml(sigFields),[sigFields]);

  const updateField = useCallback((k,v)=>{
    setSigFields(p=>({...p,[k]:v}));
    if (k==='logo_url') setLogoError(false);
  },[]);

  const saveSignature = useCallback(async()=>{
    setSigSaving(true);
    try {
      await authAPI.updateSignature({signature_html:sigHtml,signature_fields:sigFields});
      toast.success(t('profile.toasts.signatureSaved'));
    } catch(err){ toast.error(err.response?.data?.message||t('profile.toasts.saveFailed')); }
    setSigSaving(false);
  },[sigHtml,sigFields]);

  const onPickLogo = useCallback(async e=>{
    const file=e.target.files?.[0]; e.target.value='';
    if (!file) return;
    setLogoUploading(true); setLogoError(false);
    try {
      const fd=new FormData(); fd.append('logo',file);
      const {data}=await authAPI.uploadSignatureLogo(fd);
      const next={...sigFields,logo_url:data.logo_url};
      setSigFields(next);
      await authAPI.updateSignature({signature_html:buildSignatureHtml(next),signature_fields:next});
      toast.success(t('profile.toasts.logoUploaded'));
    } catch(err){ toast.error(err.response?.data?.message||t('profile.toasts.uploadFailed')); }
    setLogoUploading(false);
  },[sigFields]);

  const removeLogo = useCallback(async()=>{
    try {
      await authAPI.deleteSignatureLogo();
      const next={...sigFields,logo_url:''};
      setSigFields(next); setLogoError(false);
      await authAPI.updateSignature({signature_html:buildSignatureHtml(next),signature_fields:next});
      toast.success(t('profile.toasts.logoRemoved'));
    } catch(err){ toast.error(err.response?.data?.message||t('profile.toasts.removeFailed')); }
  },[sigFields]);

  const changePassword = useCallback(async()=>{
    if (!pwForm.currentPassword||!pwForm.newPassword){ toast.error(t('profile.toasts.currentAndNewRequired')); return; }
    if (pwForm.newPassword.length<6){ toast.error(t('profile.toasts.minLength')); return; }
    if (pwForm.newPassword!==pwForm.confirmPassword){ toast.error(t('profile.toasts.passwordsMismatch')); return; }
    setPwLoading(true);
    try {
      await authAPI.changePassword({currentPassword:pwForm.currentPassword,newPassword:pwForm.newPassword});
      toast.success(t('profile.toasts.passwordChanged'));
      setPwForm({currentPassword:'',newPassword:'',confirmPassword:''});
    } catch(err){ toast.error(err.response?.data?.message||t('profile.toasts.passwordFailed')); }
    setPwLoading(false);
  },[pwForm]);

  const initials = useMemo(()=>{
    const name=user?.first_name?`${user.first_name} ${user.last_name||''}`:user?.name||'';
    return name.split(' ').map(n=>n[0]||'').join('').toUpperCase().slice(0,2)||'U';
  },[user]);

  const displayName = user?.first_name?`${user.first_name} ${user.last_name||''}`:user?.name||t('profile.displayFallback');
  const formatRole  = r=>(r||'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
  const strength    = getPwStrength(pwForm.newPassword);

  return (
    <div className="pr-root">
      <div className="pr-inner">

        {/* ── Hero ── */}
        <div className="pr-hero">
          <div className="pr-av">{initials}</div>
          <div className="pr-hero-text">
            <h1>{displayName}</h1>
            <p>
              {user?.email}
              <span className="role-chip">{formatRole(user?.role)}</span>
            </p>
          </div>
        </div>

        {/* ── Card ── */}
        <div className="pr-card">

          {/* Tabs */}
          <div className="pr-tabs">
            <button type="button" className={`pr-tab${tab==='signature'?' on':''}`} onClick={()=>setTab('signature')}>
              <FiEdit3 size={14}/> {t('profile.tabs.emailSignature')}
            </button>
            <button type="button" className={`pr-tab${tab==='password'?' on':''}`} onClick={()=>setTab('password')}>
              <FiLock size={14}/> {t('profile.tabs.changePassword')}
            </button>
            <button type="button" className={`pr-tab${tab==='zoom'?' on':''}`} onClick={()=>setTab('zoom')}>
              <FiVideo size={14}/> {t('profile.tabs.zoomHost')}
            </button>
          </div>

          {/* ══ SIGNATURE TAB ══ */}
          {tab==='signature' && (
            <>
              {sigLoading ? (
                <div className="spin-center"><div className="spin-ring"/></div>
              ) : (
                <>
                  {/* ── TOP: Email preview ── */}
                  <div className="sig-preview-top">
                    <div className="preview-header-row">
                      <div className="preview-lbl">
                        <span className="live-dot"/>
                        {t('profile.signature.livePreview')}
                      </div>
                      <span style={{fontSize:11,color:'var(--muted)',fontWeight:600,letterSpacing:'0.03em'}}>
                        {t('profile.signature.appendedToEmail')}
                      </span>
                    </div>

                    <div className="email-mockup">
                      {/* title bar */}
                      <div className="email-mockup-titlebar">
                        <span className="em-dot" style={{background:'#ff5f57'}}/>
                        <span className="em-dot" style={{background:'#febc2e'}}/>
                        <span className="em-dot" style={{background:'#28c840'}}/>
                        <span style={{marginLeft:10,fontSize:12,color:'var(--muted)',fontWeight:500}}>
                          {t('profile.signature.composeNewMessage')}
                        </span>
                      </div>

                      {/* email meta */}
                      <div className="email-mockup-meta">
                        <div className="email-meta-row">
                          <strong>{t('profile.signature.from')}</strong>
                          {sigFields.full_name||displayName} &lt;{sigFields.email||user?.email||'you@company.com'}&gt;
                        </div>
                        <div className="email-meta-row">
                          <strong>{t('profile.signature.to')}</strong> {t('profile.signature.sampleTo')}
                        </div>
                      </div>

                      <div className="email-subject">{t('profile.signature.sampleSubject')}</div>

                      <div className="email-body-text">
                        {t('profile.signature.sampleBody1')}
                        {' '}{t('profile.signature.sampleBody2')}
                        <br/><br/>
                        {t('profile.signature.sampleBody3')}
                      </div>

                      <hr className="sig-divider"/>

                      <div className="sig-render-zone">
                        {sigHtml ? (
                          <div dangerouslySetInnerHTML={{__html:sigHtml}}/>
                        ) : (
                          <div className="sig-empty">
                            <FiMail size={30}/>
                            {t('profile.signature.emptyHint')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── BOTTOM: Fields ── */}
                  <div className="details-section">
                    <div className="sec-hd">
                      <div className="sec-num">1</div>
                      <span className="sec-lbl">{t('profile.signature.yourDetails')}</span>
                      <span className="sec-line"/>
                    </div>

                    <div className="fg-row">
                      <FF label={t('profile.signature.fullName')} value={sigFields.full_name}
                        onChange={v=>updateField('full_name',v)} placeholder={t('profile.signature.namePlaceholder')}/>
                      <FF label={t('profile.signature.titlePosition')} value={sigFields.title}
                        onChange={v=>updateField('title',v)} placeholder={t('profile.signature.titlePlaceholder')}/>
                    </div>

                    <div className="fg-row">
                      <FF label={t('profile.signature.directPhone')} icon={<FiPhone size={13}/>}
                        value={sigFields.phone} onChange={v=>updateField('phone',v)}
                        placeholder={t('profile.signature.phonePlaceholder')} type="tel"/>
                      <FF label={t('profile.signature.mobileWhatsApp')} icon={<FiPhone size={13}/>}
                        value={sigFields.mobile} onChange={v=>updateField('mobile',v)}
                        placeholder={t('profile.signature.mobilePlaceholder')} type="tel"/>
                    </div>

                    <div className="fg-row">
                      <FF label={t('profile.signature.emailAddress')} icon={<FiMail size={13}/>}
                        value={sigFields.email} onChange={v=>updateField('email',v)}
                        placeholder={t('profile.signature.emailPlaceholder')} type="email"/>
                      <FF label={t('profile.signature.website')} icon={<FiGlobe size={13}/>}
                        value={sigFields.website} onChange={v=>updateField('website',v)}
                        placeholder={t('profile.signature.websitePlaceholder')}/>
                    </div>

                    <div className="fg-row s1" style={{marginBottom:0}}>
                      <FF label={t('profile.signature.addressLabel')} icon={<FiMapPin size={13}/>}
                        value={sigFields.address} onChange={v=>updateField('address',v)}
                        placeholder={t('profile.signature.addressPlaceholder')}/>
                    </div>
                  </div>

                  {/* ── Logo ── */}
                  <div className="logo-section">
                    <div className="sec-hd">
                      <div className="sec-num">2</div>
                      <span className="sec-lbl">{t('profile.signature.companyLogo')}</span>
                      <span className="sec-line"/>
                    </div>
                    <div className="logo-inner">
                      <div className="logo-actions">
                        <p className="logo-hint">
                          {t('profile.signature.logoUploadHint')}
                        </p>
                        <div className="logo-btns">
                          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                            id="sig-logo-file" style={{display:'none'}}
                            disabled={logoUploading} onChange={onPickLogo}/>
                          <label htmlFor="sig-logo-file" className="btn-ghost"
                            style={{cursor:logoUploading?'wait':'pointer',opacity:logoUploading?0.6:1}}>
                            <FiUpload size={14}/>
                            {logoUploading ? <>{t('profile.signature.uploadingDots')}<Dots/></> : t('profile.signature.chooseImage')}
                          </label>
                          {sigFields.logo_url && (
                            <button type="button" className="btn-danger-ghost" onClick={removeLogo} disabled={logoUploading}>
                              <FiTrash2 size={14}/> {t('profile.signature.removeLogo')}
                            </button>
                          )}
                        </div>
                        <div className="width-row">
                          <span className="width-lbl">{t('profile.signature.logoWidth')}</span>
                          <input type="number" min="40" max="200" className="width-num"
                            value={sigFields.logo_width}
                            onChange={e=>updateField('logo_width',e.target.value)}/>
                        </div>
                      </div>
                      <div className="logo-thumb-box">
                        {sigFields.logo_url && !logoError ? (
                          <img src={sigFields.logo_url} alt="Logo" className="logo-thumb"
                            onError={()=>setLogoError(true)}/>
                        ) : (
                          <div className="logo-empty-box">
                            <FiImage size={18}/>
                            <span>{t('profile.signature.noLogo')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="pr-footer">
                <button type="button" className="btn-prime" onClick={saveSignature} disabled={sigSaving}>
                  <FiSave size={14}/>
                  {sigSaving ? <>{t('profile.signature.savingSignature')}<Dots/></> : t('profile.signature.saveSignature')}
                </button>
              </div>
            </>
          )}

          {/* ══ PASSWORD TAB ══ */}
          {tab==='password' && (
            <>
              <div className="pw-card-head">
                <div className="ch-icon amber"><FiShield size={21}/></div>
                <div className="ch-text">
                  <h4>{t('profile.password.title')}</h4>
                  <p>{t('profile.password.hint')}</p>
                </div>
              </div>

              <div className="pw-body">
                <div className="pw-form">

                  <div className="fg">
                    <span className="fg-label">{t('profile.password.currentPassword')}</span>
                    <div className="fi-wrap">
                      <input type={showCur?'text':'password'} className="f-input"
                        value={pwForm.currentPassword}
                        onChange={e=>setPwForm(f=>({...f,currentPassword:e.target.value}))}
                        placeholder={t('profile.password.currentPlaceholder')}
                        style={{paddingRight:42}}/>
                      <button type="button" className="pw-toggle-btn" onClick={()=>setShowCur(v=>!v)}>
                        {showCur?<FiEyeOff size={15}/>:<FiEye size={15}/>}
                      </button>
                    </div>
                  </div>

                  <div className="fg">
                    <span className="fg-label">{t('profile.password.newPassword')}</span>
                    <div className="fi-wrap">
                      <input type={showNew?'text':'password'} className="f-input"
                        value={pwForm.newPassword}
                        onChange={e=>setPwForm(f=>({...f,newPassword:e.target.value}))}
                        placeholder={t('profile.password.newPlaceholder')}
                        style={{paddingRight:42}}/>
                      <button type="button" className="pw-toggle-btn" onClick={()=>setShowNew(v=>!v)}>
                        {showNew?<FiEyeOff size={15}/>:<FiEye size={15}/>}
                      </button>
                    </div>
                    {pwForm.newPassword && (
                      <div className="str-bar">
                        <div className="str-fill"
                          style={{width:STR_W[strength-1]||'0%',background:STR_COL[strength-1]||'transparent'}}/>
                      </div>
                    )}
                  </div>

                  <div className="fg">
                    <span className="fg-label">{t('profile.password.confirmPassword')}</span>
                    <div className="fi-wrap">
                      <input type="password" className="f-input"
                        value={pwForm.confirmPassword}
                        onChange={e=>setPwForm(f=>({...f,confirmPassword:e.target.value}))}
                        placeholder={t('profile.password.confirmPlaceholder')}
                        style={{paddingRight:42}}/>
                      {pwForm.confirmPassword && pwForm.newPassword===pwForm.confirmPassword && (
                        <span className="match-check"><FiCheck size={15}/></span>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              <div className="pr-footer">
                <button type="button" className="btn-prime amber" onClick={changePassword} disabled={pwLoading}>
                  <FiLock size={14}/>
                  {pwLoading ? <>{t('profile.password.changingBtn')}<Dots/></> : t('profile.password.changeBtn')}
                </button>
              </div>
            </>
          )}

          {tab === 'zoom' && (
            <>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>
                {t('profile.zoom.desc')}
              </p>
              <div className="fg">
                <span className="fg-label">{t('profile.zoom.label')}</span>
                <div className="fi-wrap">
                  <span className="fi-icon"><FiVideo size={16} /></span>
                  <input
                    type="email"
                    className="f-input ico"
                    value={zoomMeetingEmail}
                    onChange={(e) => setZoomMeetingEmail(e.target.value)}
                    placeholder={t('profile.zoom.placeholder')}
                  />
                </div>
              </div>
              <div className="pr-footer" style={{ marginTop: 20 }}>
                <button type="button" className="btn-prime" onClick={saveZoomHostEmail} disabled={zoomSaving}>
                  <FiSave size={14} />
                  {zoomSaving ? <>{t('profile.zoom.savingBtn')}<Dots /></> : t('profile.zoom.saveBtn')}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default Profile;